// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { withHandlers } from 'recompose';
import constants from 'logic/data/Izenda.ReportDesignerConstants';
import reportConstants from 'logic/data/Izenda.ReportsConstants';
import uiState from 'logic/Izenda.UiState.js';
import routeUtils from 'share/utils/Izenda.RouteUtils';
import { curry, isBoolean, isNil, constant, isEmpty } from 'lodash/fp';
import { ALERT_TYPES, SHOW_TYPES } from 'logic/data/Izenda.AlertConstants';

var reportDesignerActions = withHandlers({
  disableReport: curry(disableReport),
  onPageChanging: curry(onPageChanging),
  saveDraftReport: curry(saveDraftReport),
  detectReportChange: curry(detectReportChange),
  handleTenantChange: curry(handleTenantChange),
  handleCascadingChanged: curry(handleCascadingChanged),
  handleNeedUpdateResultChanged: curry(handleNeedUpdateResultChanged)
});

/**
 * On page changing action that happens when user navigates to other section
 * of report designer.
 */
function onPageChanging(props: any, nextPage: string, currentPage: string): void {
  // Return if pageName is active
  const menuStore = props.menuStore;
  if (menuStore.contextMenuActiveLink === nextPage) {
    return;
  }
  menuStore.changeActiveLink(nextPage);
}

/**
 * Save draft report
 */
function saveDraftReport(props: any, e: any): any {
  const {
    store,
    store: { model }
  } = props;
  model.setIgnoreCheckChange();

  return store
    .saveDraftReport(props.pageName, isBoolean(e) && e)
    .then(result => (result.isDisableReport ? disableReport(props) : result.success));
}

/**
 * Save draft report only when report physical data is unchanged.
 */
function saveDraftIfNeeded(props: any, pageName: string): void {
  confirmWhenErrors(props);

  return detectReportChange(props, pageName).then(result => {
    if (isNil(result) || result.success) {
      pageName === constants.PAGE_NAME.DATA_SOURCE
        ? uiState.enableSystemSetting()
        : uiState.updateDisableSystemSetting();
    }
    return isNil(result) || result.success ? saveDraftReport(props) : null;
  });
}

/**
 * Disable report action in case of report being invalid
 */
function disableReport(props: any, _: any): boolean {
  props.menuStore.updateMenuState(false);
  props.store.disable();
  return false;
}

/**
 * Confirm user when detected report data changed.
 */
function confirmWhenErrors(props: any): any {
  const confirmFn = props.confirmFn;
  return result => {
    const success = result.success;
    if (success) {
      return true;
    }
    const error = result.messages[0];
    switch (error.key) {
      case constants.DETECT_CHANGE.PHYSICAL:
        return confirmFn('Information', {
          description: error.messages[0],
          type: 'info',
          showCloseIndicator: true
        }).then(constant(false));
      case constants.DETECT_CHANGE.LOGICAL:
        return confirmFn('Confirmation', {
          description: error.messages[0]
        }).then(constant(true));
      default:
        return false;
    }
  };
}

/**
 * Detect report change before save
 * Validation steps:
 *   + Detect report changes
 *   + Validate data object limitation
 *     -> invalid: disable report
 *   + Check Filter rules
 * @return {Promise} { success: boolean, messages: messages }
 */
function detectReportChange(props: any, pageName: string): any {
  const store = props.store;

  return store
    .detectReportChange(pageName)
    .then(result => {
      if (result.success) {
        return Promise.resolve({ success: true });
      }

      const error = result.messages[0];
      switch (error.key) {
        case constants.DETECT_CHANGE.PHYSICAL:
          return props
            .confirmFn('Information', {
              description: error.messages[0],
              type: 'info',
              showCloseIndicator: true
            })
            .then(disableReport.bind(null, props));
        case constants.DETECT_CHANGE.LOGICAL:
          return props
            .confirmFn('Confirmation', {
              description: error.messages[0]
            })
            .then(result => {
              if (result && result.success) {
                return checkFilterRules(props);
              }
              return result ? result : { success: true };
            });
        case '':
          return props
            .confirmFn('Information', {
              description: error.messages[0],
              type: 'info',
              showCloseIndicator: true
            })
            .then(() => {
              return { success: false };
            });
        default:
          return result;
      }
    })
    .then(result => {
      return result && result.success ? store.checkDataObjectLimitation() : result;
    })
    .then(result => {
      if (result && result.success) {
        return checkShouldUpdateResults(props);
      }
      return result ? result : { success: true };
    });
}

/**
 * check should update results
 * @return {Promise} { success: true/false }
 */
function checkShouldUpdateResults(props: any): any {
  if (props.menuStore.contextMenuActiveLink === constants.PAGE_NAME.DATA_SOURCE) {
    var isDirty = checkDataSourceDirty(props);
    return isDirty.success ? checkFilterRules(props) : isDirty;
  }

  return checkFilterRules(props);
}

/**
 * check filter rules
 * @return {Promise} { success: true/false }
 */
function checkDataSourceDirty(props: any) {
  var dataSourceDetail = props.store.dataSourceStore.dataSourceDetail;

  var message = dataSourceDetail.isDataTreeDirty ? reportConstants.MESSAGES.NEED_UPDATE_RESULTS : null;

  if (message) {
    props.confirmFn('Information', {
      description: message,
      type: 'info',
      showCloseIndicator: true,
      isHTMLFormat: true
    });
    return { success: false };
  }

  return { success: true };
}

/**
 * check filter rules
 * @return {Promise} { success: true/false }
 */
function checkFilterRules(props: any): any {
  const reportFilters = props.store.fieldsStore.reportFilters;

  // Change filters' requiredFilterNotApply to false when Apply filter button is pressed
  reportFilters.filterFields.forEach(filter => (filter.requiredFilterNotApply = false));

  let message = null;

  // for SP filters
  if (!isEmpty(reportFilters.emptyRequiredParamFilters)) {
    message = reportConstants.MESSAGES.EMPTY_FILTER_PARAM(reportFilters.emptyRequiredParamFilters);
  }

  // For normal filters
  if (isNil(message) && !isEmpty(reportFilters.emptyNotVisibleRequiredFilter)) {
    message = reportConstants.MESSAGES.EMPTY_REQUIRED_FILTER(reportFilters.emptyNotVisibleRequiredFilter);
  }

  // Need Update Results
  if (isNil(message) && reportFilters.needUpdateResult) {
    message = reportConstants.MESSAGES.NEED_UPDATE_RESULTS;
  }

  if (message) {
    props.confirmFn('Information', {
      description: message,
      type: 'info',
      showCloseIndicator: true,
      isHTMLFormat: true
    });
    return { success: false };
  }

  return { success: true };
}

/**
 * handle tenant changing
 */
function handleTenantChange(props: any, change: any): void {
  !uiState.keepState &&
    props.router.push({
      pathname: routeUtils.getReportDesignerUrl()
    });
}

/**
 * Handle cascading change
 * @param {Object} props
 * @param {boolean} change
 */
function handleCascadingChanged(props: any, change: boolean): void {
  change &&
    props.confirmFn('Information', {
      type: 'info',
      description: constants.CASCADING_CHANGED_MESSAGE,
      showCloseIndicator: true
    });
}

/**
 * Handle need update result changed
 * @param {Object} props
 * @param {boolean} change
 */
function handleNeedUpdateResultChanged(props: {}, change: boolean) {
  const { store: { alertDetails }, localization } = props;

  if (change) {
    alertDetails.set({
      message: localization.getText('REPORTDESIGNER_UPDATE_RESULT'),
      type: ALERT_TYPES.INFO,
      success: true,
      showType: SHOW_TYPES.ALWAYS
    });
  } else {
    alertDetails.reset();
  }
}

export default reportDesignerActions;



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Izenda.ReportDesignerActions.js